/**
 * Export Utilities
 * exportWebHtml, exportEmailHtml, captureA4Image, captureA4Pdf,
 * exportZipBundle, copyHtmlToClipboard
 */
import { toPng } from 'html-to-image';
// jsPDF v4 exports the class as both default and named export.
// Using the named import works across v2, v3, and v4.
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';

import { sanitizeHtml } from './sanitize';

// ─── HTML builders ────────────────────────────────────────────────────────────

function buildEmailHtml(title, sections, meta) {
  const bg = meta.backgroundColor || '#f3f4f6';
  const width = meta.contentWidth || 680;
  const font = meta.fontFamily || 'Arial, sans-serif';

  const sectionsHtml = sections
    .filter((s) => !s.mobileOverrides?.hidden)
    .map((s) => {
      const st = s.settings || {};
      return `
        <tr>
          <td style="
            padding: ${st.padding || '16px'};
            background-color: ${st.backgroundColor || '#ffffff'};
            color: ${st.textColor || '#111827'};
            font-size: ${st.fontSize || '16px'};
            font-family: ${font};
            text-align: ${st.textAlign || 'left'};
          ">
            ${sanitizeHtml(s.html || s.content || '', 'email')}
          </td>
        </tr>`;
    })
    .join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
  <title>${sanitizeHtml(title, 'email')}</title>
  <!--[if mso]><xml><o:OfficeDocumentSettings><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
</head>
<body style="margin:0;padding:0;background-color:${bg};font-family:${font};">
  <center>
    <table role="presentation" cellpadding="0" cellspacing="0" border="0"
           style="max-width:${width}px;width:100%;background-color:#ffffff;margin:0 auto;">
      ${sectionsHtml}
    </table>
  </center>
</body>
</html>`;
}

function buildWebHtml(title, sections, meta) {
  const bg = meta.backgroundColor || '#f3f4f6';
  const width = meta.contentWidth || 680;
  const font = meta.fontFamily || 'Inter, sans-serif';

  const sectionsHtml = sections
    .map((s) => {
      const st = s.settings || {};
      return `
        <section style="
          padding: ${st.padding || '16px'};
          background-color: ${st.backgroundColor || '#ffffff'};
          color: ${st.textColor || '#111827'};
          font-size: ${st.fontSize || '16px'};
          font-family: ${font};
          text-align: ${st.textAlign || 'left'};
          border-radius: ${st.borderRadius || '0'};
        ">
          ${sanitizeHtml(s.html || s.content || '', 'preview')}
        </section>`;
    })
    .join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${sanitizeHtml(title, 'email')}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{background:${bg};font-family:${font};padding:24px 0}
    .wrapper{max-width:${width}px;margin:0 auto}
    img{max-width:100%;height:auto}
    @media(max-width:600px){.wrapper{padding:0 12px}}
  </style>
</head>
<body>
  <div class="wrapper">
    ${sectionsHtml}
  </div>
</body>
</html>`;
}

// ─── Outlook-safe HTML builder ────────────────────────────────────────────────
// Table-based, fully inline styles, MSO conditionals — safe for Outlook 2016+

function buildOutlookHtml(title, sections, meta) {
  const bg    = meta.backgroundColor || '#f3f4f6';
  const width = meta.contentWidth || 680;
  const font  = meta.fontFamily || 'Arial, sans-serif';

  const sectionRows = sections
    .filter((s) => !s.mobileOverrides?.hidden)
    .map((s) => {
      const st  = s.settings || {};
      const pad = st.padding || '16px';
      // Decompose padding into top/right/bottom/left for Outlook MSO
      const [pt = '16px', pr = '16px', pb = '16px', pl = '16px'] = pad.split(' ');
      const content = s.html || s.content || '';

      return `
        <!--[if mso]>
        <tr>
          <td bgcolor="${st.backgroundColor || '#ffffff'}"
              style="padding:${pt} ${pr} ${pb} ${pl};mso-line-height-rule:exactly;"
              valign="top">
        <![endif]-->
        <!--[if !mso]><!-->
        <tr>
          <td style="
            padding:${pad};
            background-color:${st.backgroundColor || '#ffffff'};
            color:${st.textColor || '#111827'};
            font-family:${font};
            font-size:${st.fontSize || '15px'};
            line-height:1.6;
            text-align:${st.textAlign || 'left'};
            mso-line-height-rule:exactly;
          ">
        <!--<![endif]-->
            ${content}
          </td>
        </tr>`;
    })
    .join('\n');

  const safeTitle = (title || 'Newsletter').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN"
  "http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">
<html xmlns="http://www.w3.org/1999/xhtml"
      xmlns:v="urn:schemas-microsoft-com:vml"
      xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta name="x-apple-disable-message-reformatting" />
  <title>${safeTitle}</title>
  <!--[if mso]>
  <xml>
    <o:OfficeDocumentSettings>
      <o:AllowPNG/>
      <o:PixelsPerInch>96</o:PixelsPerInch>
    </o:OfficeDocumentSettings>
  </xml>
  <style type="text/css">
    table { border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    td, th { border-collapse: collapse; mso-line-height-rule: exactly; }
    img { -ms-interpolation-mode: bicubic; border: 0; }
  </style>
  <![endif]-->
  <style type="text/css">
    body, #bodyTable { margin: 0; padding: 0; background-color: ${bg}; }
    img { border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; }
    table { border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    @media only screen and (max-width: 600px) {
      #outerTable, #innerTable { width: 100% !important; max-width: 100% !important; }
      td { padding-left: 12px !important; padding-right: 12px !important; }
    }
  </style>
</head>
<body style="margin:0;padding:0;background-color:${bg};">
  <table id="bodyTable" role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
         style="background-color:${bg};margin:0;padding:0;">
    <tr>
      <td align="center" valign="top">
        <!--[if mso]>
        <table id="outerTable" role="presentation" align="center" cellpadding="0" cellspacing="0" border="0" width="${width}">
        <![endif]-->
        <!--[if !mso]><!-->
        <table id="outerTable" role="presentation" align="center" cellpadding="0" cellspacing="0" border="0"
               style="max-width:${width}px;width:100%;">
        <!--<![endif]-->
          ${sectionRows}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

// ─── Export functions ─────────────────────────────────────────────────────────

export function exportOutlookHtml(title, sections, meta) {
  const html = buildOutlookHtml(title, sections, meta);
  download(html, `${slug(title)}-outlook.html`, 'text/html');
}

export function exportEmailHtml(title, sections, meta) {
  const html = buildEmailHtml(title, sections, meta);
  download(html, `${slug(title)}-email.html`, 'text/html');
}

export function exportWebHtml(title, sections, meta) {
  const html = buildWebHtml(title, sections, meta);
  download(html, `${slug(title)}-web.html`, 'text/html');
}

// 1x1 transparent PNG. html-to-image falls back to this when a resource
// (e.g. a cross-origin image it couldn't fetch as base64, such as one served
// from a different host during local dev) fails to embed. Without an
// explicit placeholder it falls back to an empty string, which makes the
// browser reload the current page as an "image" — that fails to decode and
// rejects the whole export with a bare Event instead of a real Error.
const BLANK_IMAGE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

// CSS `column-count` (ProjectUpdateSection's multi-column body) lays out
// correctly on the live page, but browsers have a long-standing bug
// rendering multi-column content inside the SVG <foreignObject> that
// html-to-image captures through — column 2 overlaps column 1 instead of
// sitting beside it. There's no CSS-only fix, so for the duration of the
// capture we rebuild the same visual columns as explicit side-by-side
// blocks (a CSS grid of plain, single-column divs) instead of relying on
// the browser's own column-balancing, then restore the original markup.
function splitColumnsForExport(root) {
  const restores = [];

  root.querySelectorAll('.pu-content').forEach((el) => {
    const n = parseInt(el.style.columnCount, 10);
    if (!n || n < 2) return;

    // Atomic split units: each top-level list item stands on its own (so a
    // long list can still break mid-list across columns, same as
    // column-count would); anything else is a unit by itself. `indexInList`
    // is the item's 1-based position in its ORIGINAL list, so a list that
    // gets split across columns can resume numbering correctly.
    const units = [];
    Array.from(el.children).forEach((child) => {
      const tag = child.tagName;
      if (tag === 'OL' || tag === 'UL') {
        Array.from(child.children).forEach((li, idx) => {
          units.push({ node: li, listTag: tag, indexInList: idx + 1 });
        });
      } else {
        units.push({ node: child, listTag: null, indexInList: null });
      }
    });
    if (units.length < 2) return;

    const savedHtml = el.innerHTML;
    const savedStyle = el.getAttribute('style') || '';

    // Measure natural (single-column) heights before touching structure —
    // multi-column layout lays items out side by side, which would make
    // their positions (not just heights) misleading to read here.
    el.style.columnCount = '1';
    const heights = units.map((u) => u.node.getBoundingClientRect().height);
    const target = heights.reduce((a, b) => a + b, 0) / n;

    // Greedy split preserving reading order — matches column-count's own
    // top-to-bottom-then-next-column flow.
    const buckets = Array.from({ length: n }, () => []);
    let bucketIdx = 0;
    let bucketHeight = 0;
    units.forEach((u, i) => {
      if (bucketIdx < n - 1 && buckets[bucketIdx].length > 0 && bucketHeight + heights[i] > target) {
        bucketIdx += 1;
        bucketHeight = 0;
      }
      buckets[bucketIdx].push(u);
      bucketHeight += heights[i];
    });

    // Reparent the real nodes into per-column stacks; a list split across
    // columns becomes a fresh <ol>/<ul> per column, continuing numbering via
    // `start`. CSS still targets these by tag under `.pu-content` (see the
    // <style> block above), so no class copying is needed.
    const columnDivs = buckets.map((bucket) => {
      const colDiv = document.createElement('div');
      let currentList = null;
      let currentListTag = null;
      bucket.forEach((u) => {
        if (u.listTag) {
          if (currentListTag !== u.listTag) {
            currentList = document.createElement(u.listTag.toLowerCase());
            if (u.listTag === 'OL' && u.indexInList > 1) {
              currentList.setAttribute('start', String(u.indexInList));
            }
            colDiv.appendChild(currentList);
            currentListTag = u.listTag;
          }
          currentList.appendChild(u.node);
        } else {
          currentList = null;
          currentListTag = null;
          colDiv.appendChild(u.node);
        }
      });
      return colDiv;
    });

    el.innerHTML = '';
    el.style.columnCount = '';
    el.style.columnGap = '';
    el.style.display = 'grid';
    el.style.gridTemplateColumns = `repeat(${n}, 1fr)`;
    el.style.gap = '0 28px';
    el.style.alignItems = 'start';
    columnDivs.forEach((d) => el.appendChild(d));

    restores.push([el, savedHtml, savedStyle]);
  });

  return () => {
    restores.forEach(([el, html, style]) => {
      el.innerHTML = html;
      el.setAttribute('style', style);
    });
  };
}

export async function captureA4Image(canvasRef, pixelRatio = 2) {
  if (!canvasRef?.current) throw new Error('Canvas ref not ready');
  const bg = getComputedStyle(canvasRef.current).backgroundColor || '#ffffff';
  const restoreColumns = splitColumnsForExport(canvasRef.current);
  try {
    const dataUrl = await toPng(canvasRef.current, {
      quality: 0.95,
      pixelRatio,
      backgroundColor: bg,
      imagePlaceholder: BLANK_IMAGE,
    });
    return dataUrl;
  } finally {
    restoreColumns();
  }
}

export async function captureA4Pdf(canvasRef, title, pixelRatio = 2) {
  const dataUrl = await captureA4Image(canvasRef, pixelRatio);
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth  = pdf.internal.pageSize.getWidth();   // 210 mm
  const pageHeight = pdf.internal.pageSize.getHeight();  // 297 mm

  // Get natural image dimensions
  const img = new Image();
  await new Promise((res) => { img.onload = res; img.src = dataUrl; });
  const imgRatio = img.height / img.width;
  const renderedHeight = pageWidth * imgRatio;

  if (renderedHeight <= pageHeight) {
    pdf.addImage(dataUrl, 'PNG', 0, 0, pageWidth, renderedHeight);
  } else {
    // Multi-page: slice the image into A4-sized chunks
    let offsetY = 0;
    while (offsetY < img.height) {
      const sliceH = Math.min(img.height - offsetY, Math.round(img.width * (pageHeight / pageWidth)));
      const canvas = document.createElement('canvas');
      canvas.width  = img.width;
      canvas.height = sliceH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, offsetY, img.width, sliceH, 0, 0, img.width, sliceH);
      const sliceDataUrl = canvas.toDataURL('image/png');
      if (offsetY > 0) pdf.addPage();
      pdf.addImage(sliceDataUrl, 'PNG', 0, 0, pageWidth, pageHeight);
      offsetY += sliceH;
    }
  }

  pdf.save(`${slug(title)}.pdf`);
}

export async function exportZipBundle(title, sections, meta, canvasRef) {
  const zip = new JSZip();
  zip.file('email.html',   buildEmailHtml(title, sections, meta));
  zip.file('web.html',     buildWebHtml(title, sections, meta));
  zip.file('sections.json', JSON.stringify(sections, null, 2));

  if (canvasRef?.current) {
    try {
      const dataUrl = await captureA4Image(canvasRef);
      const base64  = dataUrl.split(',')[1];
      zip.file('preview.png', base64, { base64: true });
    } catch { /* optional */ }
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(blob, `${slug(title)}-export.zip`);
}

export async function copyHtmlToClipboard(sections, meta) {
  const html = buildEmailHtml('Newsletter', sections, meta);
  try {
    await navigator.clipboard.writeText(html);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = html;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function slug(str) {
  return (str || 'newsletter').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function download(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  downloadBlob(blob, filename);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
